import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { restaurant } from './config/restaurant'
import { useExperienceMode } from './lib/capabilities'
import { ReservationForm } from './components/ReservationForm'

const LazyScene = lazy(() =>
  import('./components/RestaurantScene').then((module) => ({ default: module.RestaurantScene })),
)

gsap.registerPlugin(ScrollTrigger)

function Mark() {
  return (
    <span className="mark" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  )
}

function Header() {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.body.classList.add('menu-open')
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.classList.remove('menu-open')
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const close = () => setOpen(false)

  return (
    <header className="site-header">
      <a className="brand" href="#top" onClick={close}>
        <Mark />
        <span>
          <strong>{restaurant.name}</strong>
          <small>{restaurant.descriptor}</small>
        </span>
      </a>
      <button
        ref={buttonRef}
        className="menu-button"
        type="button"
        aria-controls="primary-navigation"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>{open ? 'Close' : 'Menu'}</span>
        <i aria-hidden="true" />
      </button>
      <nav id="primary-navigation" className={open ? 'nav is-open' : 'nav'} aria-label="Primary navigation">
        <a href="#story" onClick={close}>Story</a>
        <a href="#menu" onClick={close}>Menu</a>
        <a href="#experience" onClick={close}>Experience</a>
        <a className="nav-reserve" href="#reservations" onClick={close}>Reserve</a>
      </nav>
    </header>
  )
}

const posterCapture =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('poster')

function Hero() {
  const detectedMode = useExperienceMode()
  const mode = posterCapture ? 'full' : detectedMode
  const heroRef = useRef<HTMLElement>(null)
  const progress = useRef(mode === 'fallback' ? 1 : 0)
  const [sceneEnabled, setSceneEnabled] = useState(posterCapture)
  const [engaged, setEngaged] = useState(posterCapture)
  const [sceneReady, setSceneReady] = useState(false)
  const [phase, setPhase] = useState<'wide' | 'approach' | 'table'>(mode === 'fallback' ? 'table' : 'wide')

  // Warm the scene during idle time: mount the canvas hidden behind the poster
  // so chunk load, GLB decode, and shader compilation happen before the first
  // scroll. The scene pauses its frameloop after warm-up (see FrameloopGate).
  useEffect(() => {
    if (mode === 'fallback' || sceneEnabled) return
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => setSceneEnabled(true), { timeout: 2500 })
      return () => window.cancelIdleCallback(handle)
    }
    const handle = window.setTimeout(() => setSceneEnabled(true), 1200)
    return () => window.clearTimeout(handle)
  }, [mode, sceneEnabled])

  useEffect(() => {
    if (mode === 'fallback') {
      setSceneEnabled(false)
      setEngaged(false)
      setSceneReady(false)
      return
    }

    if (engaged) return
    const engage = () => {
      setSceneEnabled(true)
      setEngaged(true)
    }
    const options: AddEventListenerOptions = { passive: true, once: true }
    window.addEventListener('pointermove', engage, options)
    window.addEventListener('wheel', engage, options)
    window.addEventListener('touchstart', engage, options)
    window.addEventListener('keydown', engage, { once: true })
    return () => {
      window.removeEventListener('pointermove', engage)
      window.removeEventListener('wheel', engage)
      window.removeEventListener('touchstart', engage)
      window.removeEventListener('keydown', engage)
    }
  }, [mode, engaged])

  useLayoutEffect(() => {
    if (mode === 'fallback' || !heroRef.current) {
      progress.current = 1
      setPhase('table')
      return
    }

    let previousPhase = 'wide'
    const trigger = ScrollTrigger.create({
      trigger: heroRef.current,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.9,
      onUpdate: ({ progress: next }) => {
        progress.current = next
        const nextPhase = next < 0.48 ? 'wide' : next < 0.8 ? 'approach' : 'table'
        if (nextPhase !== previousPhase) {
          previousPhase = nextPhase
          setPhase(nextPhase)
        }
      },
    })
    return () => trigger.kill()
  }, [mode])

  return (
    <section id="top" ref={heroRef} className={`hero hero--${mode} phase-${phase}`} aria-labelledby="hero-title">
      <div className="hero-sticky">
        <div className={`hero-poster${sceneReady && engaged ? ' is-hidden' : ''}`} aria-hidden="true" />
        {mode !== 'fallback' && sceneEnabled && (
          <div className="scene-wrap">
            <Suspense fallback={null}>
              <LazyScene
                progress={progress}
                mode={mode}
                capture={posterCapture}
                active={engaged}
                onReady={() => setSceneReady(true)}
              />
            </Suspense>
          </div>
        )}
        <div className="hero-vignette" aria-hidden="true" />
        <Header />
        <div className="hero-content hero-content--wide">
          <p className="eyebrow">{restaurant.hero.eyebrow}</p>
          <h1 id="hero-title">{restaurant.hero.title}</h1>
          <p className="hero-deck">{restaurant.hero.supporting}</p>
          <div className="hero-actions">
            <a className="button button--light" href={restaurant.links.reservation}>Reserve a table</a>
            <a className="text-link text-link--light" href={restaurant.links.menu}>Explore the menu <span aria-hidden="true">↘</span></a>
          </div>
        </div>
        <div className="hero-content hero-content--table" aria-hidden={phase !== 'table'}>
          <p className="eyebrow">The table is set</p>
          <p className="hero-table-line">{restaurant.hero.sensoryLine}</p>
          <a className="button button--light" href={restaurant.links.reservation}>Reserve a table</a>
        </div>
        {mode !== 'fallback' && (
          <div className="scroll-cue" aria-hidden="true">
            <span>Scroll to enter</span>
            <i />
          </div>
        )}
      </div>
    </section>
  )
}

function Story() {
  return (
    <section id="story" className="story light-section" aria-labelledby="story-title">
      <div className="section-grid">
        <div className="section-index"><span>01</span><span>Our story</span></div>
        <div className="story-main">
          <h2 id="story-title">{restaurant.story.title}</h2>
          <p className="story-lead">{restaurant.story.lead}</p>
        </div>
        <p className="story-detail">{restaurant.story.detail}</p>
      </div>
      <div className="ingredient-line" aria-label="Culinary foundations">
        <span>Fire</span><i /><span>Spice</span><i /><span>Season</span><i /><span>Memory</span>
      </div>
    </section>
  )
}

function Menu() {
  return (
    <section id="menu" className="menu-section" aria-labelledby="menu-title">
      <div className="section-heading section-heading--dark">
        <div className="section-index"><span>02</span><span>From the kitchen</span></div>
        <div>
          <h2 id="menu-title">A menu with heat, restraint, and a sense of place.</h2>
          <p>Three signatures from a menu designed to unfold across the table.</p>
        </div>
      </div>
      <div className="dish-list">
        {restaurant.dishes.map((dish, index) => (
          <article className="dish" key={dish.name}>
            <div className="dish-copy">
              <span className="dish-number">{dish.number}</span>
              <h3>{dish.name}</h3>
              <p>{dish.description}</p>
            </div>
            <figure className={`dish-image dish-image--${index + 1}`}>
              <img src={dish.image} alt={dish.alt} width="1200" height="900" loading="eager" />
              <figcaption>Signature · {dish.name}</figcaption>
            </figure>
          </article>
        ))}
      </div>
      <a className="text-link text-link--light menu-link" href="#reservations">Plan your dinner <span aria-hidden="true">↘</span></a>
    </section>
  )
}

function Experience() {
  return (
    <section id="experience" className="experience light-section" aria-labelledby="experience-title">
      <div className="experience-visual" role="img" aria-label="Ember-lit dining room with brass screens and set tables">
        <div className="portal-crop" aria-hidden="true"><i /><i /><i /><i /></div>
        <span>Evenings at Ember & Vine</span>
      </div>
      <div className="experience-copy">
        <div className="section-index"><span>03</span><span>The experience</span></div>
        <h2 id="experience-title">Hospitality, from first light to final pour.</h2>
        <div className="experience-list">
          {restaurant.experience.map((item) => (
            <article key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function Chef() {
  return (
    <section className="chef light-section" aria-labelledby="chef-title">
      <div className="chef-rule" aria-hidden="true"><Mark /></div>
      <div className="chef-copy">
        <div className="section-index"><span>04</span><span>Culinary philosophy</span></div>
        <h2 id="chef-title">{restaurant.chef.title}</h2>
        <p>{restaurant.chef.philosophy}</p>
        {restaurant.chef.name && <p className="chef-name">{restaurant.chef.name} · {restaurant.chef.role}</p>}
      </div>
    </section>
  )
}

function Reservations() {
  return (
    <section id="reservations" className="reservations" aria-labelledby="reservations-title">
      <div className="reservation-heading">
        <div className="section-index"><span>05</span><span>Your table</span></div>
        <h2 id="reservations-title">Come hungry.<br />Stay a while.</h2>
        <p>Select your preferred evening. Our reservations team will confirm the details.</p>
      </div>
      <ReservationForm />
    </section>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-brand">
        <Mark />
        <p>{restaurant.name}</p>
        <span>{restaurant.descriptor}</span>
      </div>
      <div>
        <h2>Visit</h2>
        <address>{restaurant.contact.addressLines.map((line) => <span key={line}>{line}</span>)}</address>
        <a href={`tel:${restaurant.contact.phoneHref}`}>{restaurant.contact.phoneDisplay}</a>
        <a href={`mailto:${restaurant.contact.email}`}>{restaurant.contact.email}</a>
      </div>
      <div>
        <h2>Hours</h2>
        <dl className="hours">
          {restaurant.hours.map(([days, time]) => (
            <div key={days}><dt>{days}</dt><dd>{time}</dd></div>
          ))}
        </dl>
      </div>
      <div>
        <h2>More</h2>
        <a href={restaurant.links.reservation}>Reservations</a>
        <a href={restaurant.links.privateDining}>Private dining</a>
        <a href={restaurant.links.instagram}>Instagram</a>
      </div>
      <div className="footer-legal">
        <span>© {new Date().getFullYear()} {restaurant.name}</span>
        <span>Placeholder concept site</span>
        <a id="accessibility" href={restaurant.links.accessibility}>Accessibility</a>
        <a id="privacy" href={restaurant.links.privacy}>Privacy</a>
      </div>
    </footer>
  )
}

export default function App() {
  useEffect(() => {
    const elements = gsap.utils.toArray<HTMLElement>('[data-reveal]')
    const animations = elements.map((element) =>
      gsap.fromTo(
        element,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.62,
          ease: 'power2.out',
          scrollTrigger: { trigger: element, start: 'top 84%', once: true },
        },
      ),
    )
    return () => animations.forEach((animation) => animation.kill())
  }, [])

  return (
    <>
      <Hero />
      <main id="main-content">
        <Story />
        <Menu />
        <Experience />
        <Chef />
        <Reservations />
      </main>
      <Footer />
    </>
  )
}
